import { useState } from 'react'
import { Link } from 'react-router'
import { formatDistanceToNow } from 'date-fns'
import { ChevronLeft, ChevronRight, MapPin, Search } from 'lucide-react'
import { useAdminLocationList } from '@/hooks/useAdminLocations'
import { PageHeader } from '@/components/shared/PageHeader'
import { ThumbnailPreview } from '@/components/shared/ThumbnailPreview'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

export function LocationsListPage() {
  const [search, setSearch] = useState('')
  const [includeDeleted, setIncludeDeleted] = useState(false)
  const [page, setPage] = useState(1)
  const pageSize = 25

  const { data, isLoading } = useAdminLocationList({
    page,
    pageSize,
    search: search || undefined,
    includeDeleted,
  })

  const locations = data?.locations ?? []
  const total = data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  return (
    <div>
      <PageHeader title="Locations" description={`${total} locations across all users`} />

      <div className="mb-6 flex flex-wrap items-center gap-4">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by name, airport or user ID..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              setPage(1)
            }}
            className="pl-9"
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          <input
            type="checkbox"
            checked={includeDeleted}
            onChange={(e) => {
              setIncludeDeleted(e.target.checked)
              setPage(1)
            }}
          />
          Include deleted
        </label>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-12 rounded" />
          ))}
        </div>
      ) : locations.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
          <MapPin className="mb-4 h-12 w-12" />
          <p>No locations found.</p>
        </div>
      ) : (
        <>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[88px]">Preview</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Airport</TableHead>
                  <TableHead>Owner</TableHead>
                  <TableHead className="text-right">Layouts</TableHead>
                  <TableHead className="text-right">Updated</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {locations.map((loc) => (
                  <TableRow key={loc.id} className="cursor-pointer">
                    <TableCell>
                      <Link to={`/locations/${loc.id}`} className="block">
                        <ThumbnailPreview
                          src={loc.thumbnail_url}
                          alt={loc.name}
                          kind="location"
                          className="h-10 w-16"
                        />
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Link to={`/locations/${loc.id}`} className="font-medium hover:underline">
                        {loc.name}
                      </Link>
                      {loc.deleted_at && (
                        <Badge variant="destructive" className="ml-2 text-[10px]">
                          Deleted
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {loc.airport_code ? (
                        <span>
                          <Badge variant="secondary" className="text-[10px]">
                            {loc.airport_code}
                          </Badge>{' '}
                          {loc.airport_name ?? ''}
                        </span>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      <Link to={`/users/${loc.user_id}`} className="hover:underline">
                        {loc.owner.display_name || loc.owner.email || loc.user_id.slice(0, 8)}
                      </Link>
                      <Badge variant="outline" className="ml-2 text-[10px] capitalize">
                        {loc.owner.tier.replace('_', ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right text-sm">{loc.layout_count}</TableCell>
                    <TableCell className="text-right text-sm text-muted-foreground">
                      {formatDistanceToNow(new Date(loc.updated_at), { addSuffix: true })}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {totalPages > 1 && (
            <div className="mt-6 flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Page {page} of {totalPages}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                >
                  <ChevronLeft className="h-4 w-4" /> Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage(page + 1)}
                >
                  Next <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
